/**
 * Firestore service for list cards
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { auth, firestore } from './firebase';

import type { ListCard, ListPriority } from '@/types/lists';
import type { AddListCard, UpdateListCard } from '@/types/schemas';

// Firestore document type (raw from DB)
interface FirestoreListCard {
  list_id: string;
  oracle_id: string;
  name: string;
  quantity: number;
  priority: ListPriority;
  notes: string | null;
  added_at: Timestamp;
  scryfall_id?: string;
  preferred_scryfall_id?: string; // Legacy field — read for backward compat
}

// Convert Firestore document to client type
function firestoreToListCard(id: string, data: FirestoreListCard): ListCard {
  return {
    id,
    list_id: data.list_id,
    oracle_id: data.oracle_id,
    name: data.name,
    quantity: data.quantity ?? 1,
    priority: data.priority ?? 'medium',
    notes: data.notes ?? null,
    added_at: data.added_at?.toDate?.() ?? new Date(),
    scryfall_id: data.scryfall_id || data.preferred_scryfall_id || '',
  };
}

function getUserListCardsRef(userId: string) {
  return collection(firestore, 'users', userId, 'list_cards');
}

function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
}

/**
 * Add a card to a list.
 */
export async function addCardToList(data: AddListCard): Promise<string> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);

  const docData = {
    list_id: data.list_id,
    oracle_id: data.oracle_id,
    name: data.name,
    scryfall_id: data.scryfall_id ?? '',
    quantity: data.quantity,
    priority: data.priority,
    notes: data.notes ?? null,
    added_at: serverTimestamp(),
  };

  const docRef = await addDoc(cardsRef, docData);
  return docRef.id;
}

/**
 * Add multiple cards to a list in Firestore batches of 500.
 * Calls `onProgress` after each batch commits.
 */
export async function addCardsBatchToList(cards: AddListCard[], onProgress?: (committed: number) => void): Promise<string[]> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);
  const BATCH_SIZE = 500;
  const ids: string[] = [];
  let committed = 0;

  for (let i = 0; i < cards.length; i += BATCH_SIZE) {
    const chunk = cards.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(firestore);

    for (const data of chunk) {
      const newDocRef = doc(cardsRef);
      ids.push(newDocRef.id);
      batch.set(newDocRef, {
        list_id: data.list_id,
        oracle_id: data.oracle_id,
        name: data.name,
        scryfall_id: data.scryfall_id ?? '',
        quantity: data.quantity,
        priority: data.priority,
        notes: data.notes ?? null,
        added_at: serverTimestamp(),
      });
    }

    await batch.commit();
    committed += chunk.length;
    onProgress?.(committed);
  }

  return ids;
}

/**
 * Update a list card (quantity, priority, notes).
 * If quantity is 0, the card is removed.
 */
export async function updateListCard(cardId: string, data: UpdateListCard): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'list_cards', cardId);

  // If quantity is 0, delete the card
  if (data.quantity !== undefined && data.quantity <= 0) {
    await deleteDoc(docRef);
    return;
  }

  await updateDoc(docRef, {
    ...data,
  });
}

/**
 * Remove a card from a list.
 */
export async function removeCardFromList(cardId: string): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'list_cards', cardId);
  await deleteDoc(docRef);
}

/**
 * Get all cards in a specific list.
 */
export async function getListCards(listId: string): Promise<ListCard[]> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);
  const q = query(cardsRef, where('list_id', '==', listId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard));
}

/**
 * Get all list cards for the current user (across all lists).
 */
export async function getAllListCards(): Promise<ListCard[]> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);
  const snapshot = await getDocs(cardsRef);

  return snapshot.docs.map((docSnap) => firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard));
}

/**
 * Subscribe to real-time updates of cards in a specific list.
 * Returns an unsubscribe function.
 */
export function subscribeToListCards(uid: string, listId: string, callback: (cards: ListCard[]) => void): () => void {
  const cardsRef = collection(firestore, 'users', uid, 'list_cards');
  const q = query(cardsRef, where('list_id', '==', listId));

  return onSnapshot(q, (snapshot) => {
    const cards = snapshot.docs.map((docSnap) => firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard));
    callback(cards);
  });
}

/**
 * Subscribe to real-time updates of ALL list cards for a user.
 * Returns an unsubscribe function.
 */
export function subscribeToAllListCards(uid: string, callback: (cards: ListCard[]) => void): () => void {
  const cardsRef = collection(firestore, 'users', uid, 'list_cards');

  return onSnapshot(cardsRef, (snapshot) => {
    const cards = snapshot.docs.map((docSnap) => firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard));
    callback(cards);
  });
}

/**
 * Check if a card (by oracle_id) is already in a list.
 */
export async function isCardInList(listId: string, oracleId: string): Promise<ListCard | null> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);
  const q = query(cardsRef, where('list_id', '==', listId), where('oracle_id', '==', oracleId));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard);
}

/**
 * Get all lists that contain a specific card (by oracle_id).
 */
export async function getListsContainingCard(oracleId: string): Promise<ListCard[]> {
  const userId = getCurrentUserId();
  const cardsRef = getUserListCardsRef(userId);
  const q = query(cardsRef, where('oracle_id', '==', oracleId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => firestoreToListCard(docSnap.id, docSnap.data() as FirestoreListCard));
}
